import { addCpfMask, removeCpfMask, STATUSPAGE } from '@repo/ui';
import {
  CreateUserUseCaseInterface,
  GetUserByCpfUseCaseInterface,
  GetUserByIdUseCaseInterface,
  UpdateUserUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearSelectedAdministratorData,
  loadSelectedAdministratorData,
} from 'presentation/config/store/administrators/administrators-slice';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

export const useCreateOrUpdateAdministrator = () => {
  // Hooks
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { usersService } = useService();

  // States
  const { selectedAdministratorData } = useAppSelector(
    (state) => state.Administrators,
  );
  const [showPassword, setShowPassword] = useState(false);
  const [
    isThereIsAlreadyAnAdminWithThisCPFModalOpen,
    setIsThereIsAlreadyAnAdminWithThisCPFModalOpen,
  ] = useState(false);
  const [getUserByIdRequestStatus, setGetUserByIdRequestStatus] =
    useState<STATUSPAGE>(STATUSPAGE.DONE);
  const [getUserByCpfRequestStatus, setGetUserByCpfRequestStatus] =
    useState<STATUSPAGE>(STATUSPAGE.DONE);
  const [createOrUpdateRequestStatus, setCreateOrUpdateRequestStatus] =
    useState<STATUSPAGE>(STATUSPAGE.DONE);

  // Constants
  const areInputsAndBtnsDisabled =
    getUserByIdRequestStatus === STATUSPAGE.LOADING ||
    createOrUpdateRequestStatus === STATUSPAGE.LOADING ||
    getUserByCpfRequestStatus === STATUSPAGE.LOADING;

  const cpfSchema = Yup.string().cpfType('CPF inválido');

  // Methods
  const toggleThereIsAlreadyAnAdminWithThisCPFModal = useCallback(() => {
    setIsThereIsAlreadyAnAdminWithThisCPFModalOpen((prevState) => !prevState);
  }, []);

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prevState) => !prevState);
  }, []);

  const removeSelectedUserData = useCallback(() => {
    dispatch(clearSelectedAdministratorData());
  }, []);

  const requestCreateUser = useCallback(
    async (params: CreateUserUseCaseInterface.Params) => {
      setCreateOrUpdateRequestStatus(STATUSPAGE.LOADING);
      await usersService
        .createUser(params)
        .then((res) => {
          notifySuccess(res.message);
          navigate('/cadastros/usuarios');
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setCreateOrUpdateRequestStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestUpdateUser = useCallback(
    async (params: UpdateUserUseCaseInterface.Params) => {
      setCreateOrUpdateRequestStatus(STATUSPAGE.LOADING);
      await usersService
        .updateUser(params)
        .then((res) => {
          notifySuccess(res.message);
          navigate('/cadastros/usuarios');
        })
        .catch((err) => {
          notifyError(err.message);
          navigate('/cadastros/usuarios');
        })
        .finally(() => {
          setCreateOrUpdateRequestStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetUserById = useCallback(
    async (params: GetUserByIdUseCaseInterface.Params) => {
      setGetUserByIdRequestStatus(STATUSPAGE.LOADING);
      await usersService
        .getUserById(params)
        .then((res) => {
          dispatch(loadSelectedAdministratorData(res));
        })
        .catch((err) => {
          notifyError(err.message);
          navigate('/cadastros/usuarios');
        })
        .finally(() => {
          setGetUserByIdRequestStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      cpf: selectedAdministratorData?.cpf
        ? addCpfMask(selectedAdministratorData.cpf)
        : '',
      name: selectedAdministratorData?.name || '',
      email: selectedAdministratorData?.email || '',
      password: '',
      profile: selectedAdministratorData?.role?.id
        ? {
            value: selectedAdministratorData.role.id,
            label: selectedAdministratorData.role.name,
          }
        : null,
    },

    validationSchema: Yup.object({
      cpf: id
        ? Yup.string().notRequired()
        : Yup.string().required('Campo é obrigatório').cpfType('CPF inválido'),
      name: Yup.string().required('Campo é obrigatório'),
      email: Yup.string()
        .email('Email inválido')
        .required('Campo é obrigatório'),
      password: id
        ? Yup.string()
            .notRequired()
            .min(8, 'A senha deve conter no mínimo 8 caracteres')
            .max(32, 'A senha deve conter no máximo 32 caracteres')
            .matches(
              /^(?=.*[a-z])/,
              'A senha deve conter pelo menos uma letra minúscula',
            )
            .matches(
              /^(?=.*[A-Z])/,
              'A senha deve conter pelo menos uma letra maiúscula',
            )
            .matches(/^(?=.*\d)/, 'A senha deve conter pelo menos um número')
            .matches(
              /^(?=.*[@#$%&*!])/,
              'A senha deve conter pelo menos um caractere especial (@, #, $, %, &, *, !)',
            )
        : Yup.string()
            .required('Campo é obrigatório')
            .min(8, 'A senha deve conter no mínimo 8 caracteres')
            .matches(
              /^(?=.*[a-z])/,
              'A senha deve conter pelo menos uma letra minúscula',
            )
            .matches(
              /^(?=.*[A-Z])/,
              'A senha deve conter pelo menos uma letra maiúscula',
            )
            .matches(/^(?=.*\d)/, 'A senha deve conter pelo menos um número')
            .matches(
              /^(?=.*[@#$%&*!])/,
              'A senha deve conter pelo menos um caractere especial (@, #, $, %, &, *, !)',
            ),
      profile: Yup.object()
        .shape({
          value: Yup.number(),
          label: Yup.string(),
        })
        .required('Campo obrigatório'),
    }),

    onSubmit: (values) => {
      const commonValues = {
        name: values.name,
        email: values.email,
        password: values.password || null,
        roleId: values.profile?.value,
      };

      id
        ? requestUpdateUser({
            id,
            ...commonValues,
          })
        : requestCreateUser({
            cpf: removeCpfMask(values.cpf),
            ...commonValues,
          });
    },
  });

  const requestGetUserByCpf = useCallback(
    async (params: GetUserByCpfUseCaseInterface.Params) => {
      setGetUserByCpfRequestStatus(STATUSPAGE.LOADING);
      await usersService
        .getUserByCpf(params)
        .then((res) => {
          if (res.isAdmin) {
            toggleThereIsAlreadyAnAdminWithThisCPFModal();
            validation.resetForm();
          } else {
            validation.setFieldValue('name', res.name);
            validation.setFieldValue('email', res.email);
          }
        })
        .catch((err) => {})
        .finally(() => {
          setGetUserByCpfRequestStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  return {
    id,
    cpfSchema,
    validation,
    showPassword,
    areInputsAndBtnsDisabled,
    getUserByIdRequestStatus,
    getUserByCpfRequestStatus,
    createOrUpdateRequestStatus,
    isThereIsAlreadyAnAdminWithThisCPFModalOpen,
    navigate,
    toggleShowPassword,
    requestGetUserById,
    requestGetUserByCpf,
    removeSelectedUserData,
    toggleThereIsAlreadyAnAdminWithThisCPFModal,
  };
};
